/**
 * Socket -> feed. One of exactly two frame sources; `replay/file.ts` is the
 * other, and the renderer cannot tell them apart.
 *
 * The client never predicts. It emits an intent and waits for the server's
 * event, so the rendered frame is always a pure function of received events
 * (pure server authority).
 *
 * Connecting and joining are separate operations: the
 * latency gate has to measure the game path *before* a seat is taken, so that
 * a participant whose connection cannot carry the game is turned away rather
 * than seated and then disappointed.
 */

import { io, type Socket } from 'socket.io-client'
import type {
  AudioChunkAck,
  ClientToServerEvents,
  ConsentRecord,
  Direction,
  GameEvent,
  IceConfig,
  InputRejectionReason,
  PlayerId,
  RoomClosedPayload,
  RoomPhase,
  RtcSignal,
  ServerToClientEvents,
} from '@overcooked/shared'
import type { EventFeed } from '../feed/feed'
import {
  browserStore,
  chooseClaim,
  forgetSeat,
  holdsClaim,
  refusedAsInUse,
  rememberSeat,
  tabStore,
} from './seatClaim'
import type { PingTransport } from '../voice/latency'
import { SignalInbox } from '../voice/peerSession'

export type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>

/** What a voice link can be, as `voiceState` reports it. */
export type VoiceLinkState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed'

/**
 * The moment a key went down, on the only clock that can see input latency.
 *
 * `performance.now()` is monotonic and immune to a clock the OS steps
 * mid-round; adding `timeOrigin` puts it back on the same epoch scale as the
 * server's `wallClock`, so the tape can subtract the two. `Date.now()` is the
 * fallback for an environment without `timeOrigin` — the server accepts either
 * and stamps its own arrival time if neither arrives.
 */
function clientNow(): number {
  const origin = typeof performance !== 'undefined' ? performance.timeOrigin : NaN
  if (!Number.isFinite(origin)) return Date.now()
  return Math.round(origin + performance.now())
}

export interface RoomInfo {
  playerId: PlayerId | null
  roomCode: string | null
  players: Array<{
    id: PlayerId
    seat: number
    ready: boolean
    inSetup: boolean
    /** False while a seat is being held for somebody who dropped out. */
    connected: boolean
    /**
     * What is behind the seat. Absent from an older server, which could only
     * have been seating people.
     */
    kind?: 'human' | 'agent'
    /** The model driving an agent seat, for the lobby to name. */
    model?: string | null
  }>
  phase: RoomPhase
  /** The blob key prefix for the round about to be played, or just played. */
  sessionId: string | null
  /** Which preset this room is playing on, as the server resolved it. */
  mapName: string | null
}

export type SignalHandler = (from: PlayerId, signal: RtcSignal) => void
/** An input the server threw away before the engine saw it. */
export type RejectionHandler = (reason: InputRejectionReason) => void

export interface Connection {
  socket: GameSocket
  /** The latency gate's transport. Usable before joining. */
  ping: PingTransport
  /**
   * Take a seat in an existing room. `map` is sent for older servers and
   * ignored by current ones: the kitchen is chosen when the room is minted.
   * `consent` is the player's recording agreement, which a room with more
   * than one seat requires.
   */
  join(roomCode: string, map?: string | null, consent?: ConsentRecord | null): void
  /**
   * "Start when the others are." Also the play-again button: sent after
   * `ROUND_ENDED`, it asks the server to reset the room for another round.
   */
  ready(): void
  /**
   * "I am through the gates." Told to the server so a forced round start waits
   * for this seat rather than beginning without its keys attached.
   */
  setupDone(): void
  move(direction: Direction): void
  interact(): void
  interactRelease(): void
  sendSignal(to: PlayerId, signal: RtcSignal): void
  onSignal(handler: SignalHandler): () => void
  /** Every input the transport refused, so a press into a void can be answered. */
  onInputRejected(handler: RejectionHandler): () => void
  requestCredentials(): void
  reportRtt(rttMs: number): void
  /** One `MediaRecorder` timeslice. The server derives whose it is from us. */
  sendAudioChunk(
    payload: {
      chunk: ArrayBuffer
      seq: number
      mimeType?: string
      /** Which round's recording this is, for a tail that lands after the whistle. */
      sessionId?: string
      /** Which recorder run — a reload mid-round starts a new one. */
      recordingId?: string
    },
    /** Called with the server's answer, so an unacknowledged chunk can be resent. */
    ack?: (result: AudioChunkAck) => void,
  ): void
  /** "That is all of it" plus our own count, so the server can disagree. */
  sendAudioDone(payload: {
    lastSeq: number
    chunkCount: number
    mimeType?: string
    sessionId?: string
    recordingId?: string
    syncBeep?: boolean
  }): void
  /**
   * Whether the socket is connected right now. Audio is not emitted while it
   * is not: Socket.IO would buffer it and flush it ahead of the rejoin, where
   * the server has no seat to put it in. The resend pass sends it afterwards.
   */
  connected(): boolean
  /** This tab's voice link changed state; recorded on the tape during a round. */
  reportVoiceState(state: VoiceLinkState, relayed?: boolean): void
  disconnect(): void
}

export interface ConnectionHandlers {
  onRoom(info: RoomInfo): void
  onError(message: string): void
  onCredentials(ice: IceConfig): void
  onConnected?(): void
  /**
   * "You are already seated." Fired only when a *cold* start reclaimed a seat
   * from storage — a reload, or a tab reopened inside the grace window. The
   * caller's job is to stop showing gates and start showing the kitchen; there
   * is nothing left for the player to set up.
   *
   * Not fired when a live connection blinks and reattaches, because there the
   * player is already looking at whatever they were looking at and yanking them
   * somewhere else would be the network editing the screen.
   */
  onResumed?(): void
  /**
   * "3 … 2 … 1", or `null` for "there is no count running".
   *
   * Read off the frame stream rather than out of the feed because the feed
   * folds a *round*, and this happens before there is one. `ROUND_STARTED`
   * clears it, so the numeral cannot outlive the wait it was counting.
   */
  onCountdown?(secondsRemaining: number | null): void
  /**
   * Somebody said something, off the frame it arrived in.
   *
   * Read here rather than out of the feed for the same reason `onCountdown` is:
   * the feed folds a kitchen, and speech is not part of one — `fold.ts` declines
   * `UTTERANCE` on purpose, which is what keeps a tape with conversation in it
   * replaying identically to one without. The caller reads it aloud when the
   * other seat is an agent (`voice/speak.ts`) and ignores it otherwise.
   */
  onUtterance?(event: Extract<GameEvent, { type: 'UTTERANCE' }>): void
  /**
   * The room is gone and will not come back: a stale link, or a room the
   * server closed. The stored seat is already forgotten and the socket is
   * already closed by the time this fires, so a reconnect cannot try again.
   */
  onRoomClosed?(payload: RoomClosedPayload): void
}

const SERVER_ORIGIN = import.meta.env.PROD ? window.location.origin : 'http://localhost:3000'

/**
 * Where a seat is remembered across a reload, and across a closed tab: see
 * `seatClaim.ts`. The tab's own claim is offered as `rejoin`; the browser's,
 * which a reopened tab still has, as `reclaim`.
 */
export function hasStoredSeat(roomCode: string): boolean {
  return holdsClaim(roomCode, tabStore(), browserStore())
}

export interface ConnectOptions {
  /**
   * The room this page is looking at. A stored seat is only offered back to the
   * room it was taken in — the alternative is a tab that walks into `/r/WXYZ`
   * carrying a claim on `ABCD`.
   */
  roomCode?: string | null
}

export function connect(
  feed: EventFeed,
  handlers: ConnectionHandlers,
  options: ConnectOptions = {},
): Connection {
  const socket: GameSocket = io(SERVER_ORIGIN, { transports: ['websocket', 'polling'] })

  const info: RoomInfo = {
    playerId: null,
    roomCode: null,
    players: [],
    phase: 'lobby',
    sessionId: null,
    mapName: null,
  }
  const signalHandlers = new Set<SignalHandler>()
  // Signals that land before voice has a connection to hand them to — see
  // `SignalInbox`. Delivered to the first handler that subscribes.
  const inbox = new SignalInbox()
  const rejectionHandlers = new Set<RejectionHandler>()
  let sessionToken: string | null = null
  /**
   * Whether this page has ever held a seat, and whether a rejoin is in flight.
   *
   * The first tells a cold reclaim (a reload — the caller should skip the
   * gates) from a warm one (the socket blinked — the caller should notice
   * nothing). The second is what lets a refusal be answered quietly: the server
   * says no with an `errorMessage`, and "your seat could not be restored" is
   * not a sentence to put in front of somebody who is about to be seated
   * normally a moment later.
   */
  let everJoined = false
  let resuming: 'cold' | 'warm' | null = null
  /** Which claim the rejoin in flight was made with, so a refusal forgets the right one. */
  let resumingVia: 'rejoin' | 'reclaim' | null = null

  socket.on('connect', () => {
    const choice = chooseClaim(options.roomCode, tabStore(), browserStore())
    if (choice) {
      resuming = everJoined ? 'warm' : 'cold'
      resumingVia = choice.via
      socket.emit(choice.via, choice.claim)
    }
    handlers.onConnected?.()
  })

  socket.on('joined', ({ playerId, roomCode, sessionToken: token, sessionId, resumeToken }) => {
    info.playerId = playerId
    info.roomCode = roomCode
    info.sessionId = sessionId
    sessionToken = token
    // Written on every `joined`, including the one a rejoin produces: the token
    // is re-minted each time, and a stale expiry is a seat that stops being
    // reclaimable while its player is still sitting in it.
    rememberSeat({ roomCode, playerId, resumeToken }, tabStore(), browserStore())

    const wasResuming = resuming
    resuming = null
    resumingVia = null
    everJoined = true
    handlers.onRoom({ ...info })
    if (wasResuming === 'cold') handlers.onResumed?.()
  })

  socket.on('roomState', ({ players, phase, sessionId, mapName }) => {
    info.players = players
    info.phase = phase
    info.sessionId = sessionId
    info.mapName = mapName
    handlers.onRoom({ ...info })
  })

  socket.on('frame', (frame) => {
    for (const event of frame.events) {
      if (event.type === 'COUNTDOWN') handlers.onCountdown?.(event.secondsRemaining)
      else if (event.type === 'ROUND_STARTED') handlers.onCountdown?.(null)
      else if (event.type === 'UTTERANCE') handlers.onUtterance?.(event)
    }
    feed.push(frame)
  })
  socket.on('errorMessage', (message) => {
    // A refused rejoin is expected traffic — the grace window closes, the room
    // finishes, a stale tab is reopened — and the ordinary join is right behind
    // it. Drop the claim, say so to the console, and let the gates carry on.
    if (resuming) {
      resuming = null
      // A reclaim refused because another tab in this browser is sitting in
      // the seat leaves that tab's claim alone: it is still good, for it.
      forgetSeat(options.roomCode, tabStore(), browserStore(), {
        keepBrowser: resumingVia === 'reclaim' && refusedAsInUse(message),
      })
      resumingVia = null
      console.info(`[room] ${message} Joining as a new player instead.`)
      return
    }
    handlers.onError(message)
  })
  socket.on('roomClosed', (payload) => {
    // Terminal. The claim on the seat is worthless now, and a socket left open
    // would reconnect after a blip and ask for a room that no longer exists.
    resuming = null
    forgetSeat(payload.roomCode ?? options.roomCode, tabStore(), browserStore())
    sessionToken = null
    socket.disconnect()
    handlers.onRoomClosed?.(payload)
  })
  socket.on('voiceCredentials', (ice) => handlers.onCredentials(ice))
  socket.on('voiceSignal', ({ from, signal }) => {
    if (signalHandlers.size === 0) {
      inbox.hold(from, signal)
      return
    }
    for (const handler of [...signalHandlers]) handler(from, signal)
  })
  socket.on('inputRejected', ({ reason }) => {
    for (const handler of [...rejectionHandlers]) handler(reason)
  })

  return {
    socket,
    ping: {
      ping: (sentAt) => socket.emit('latencyPing', { sentAt }),
      onPong: (handler) => {
        const listener = ({ sentAt }: { sentAt: number }): void => handler(sentAt)
        socket.on('latencyPong', listener)
        return () => socket.off('latencyPong', listener)
      },
    },
    join: (roomCode, map, consent) =>
      socket.emit('joinRoom', { roomCode, map: map ?? undefined, consent: consent ?? undefined }),
    ready: () => socket.emit('ready'),
    setupDone: () => socket.emit('setupDone'),
    move: (direction) => socket.emit('input', { kind: 'move', direction, clientMs: clientNow() }),
    interact: () => socket.emit('input', { kind: 'interact', clientMs: clientNow() }),
    interactRelease: () => socket.emit('input', { kind: 'interact_release', clientMs: clientNow() }),
    sendSignal: (to, signal) => socket.emit('voiceSignal', { to, signal }),
    onSignal: (handler) => {
      signalHandlers.add(handler)
      // Whatever arrived while nobody was listening, in order, once the
      // subscriber has finished setting itself up.
      if (inbox.size > 0) {
        const held = inbox.drain()
        queueMicrotask(() => {
          if (!signalHandlers.has(handler)) {
            for (const entry of held) inbox.hold(entry.from, entry.signal)
            return
          }
          for (const entry of held) handler(entry.from, entry.signal)
        })
      }
      return () => signalHandlers.delete(handler)
    },
    onInputRejected: (handler) => {
      rejectionHandlers.add(handler)
      return () => {
        rejectionHandlers.delete(handler)
      }
    },
    /**
     * The refresh path, used before an ICE restart. Goes over HTTP with the
     * session token because that endpoint returns a promise the caller can
     * fail on; the socket push covers the initial mint on join, and is the
     * fallback here if the token has not arrived yet.
     */
    requestCredentials: () => {
      if (!sessionToken) {
        socket.emit('requestVoiceCredentials')
        return
      }
      void fetch('/api/ice-servers', { headers: { 'x-session-token': sessionToken } })
        .then(async (response) => {
          if (!response.ok) throw new Error(`ice-servers returned ${response.status}`)
          handlers.onCredentials((await response.json()) as IceConfig)
        })
        .catch((err: Error) => {
          console.warn('[voice] credential refresh failed, falling back to the socket:', err.message)
          socket.emit('requestVoiceCredentials')
        })
    },
    reportRtt: (rttMs) => socket.emit('rttSample', { rttMs }),
    sendAudioChunk: (payload, ack) =>
      ack ? socket.emit('audioChunk', payload, ack) : socket.emit('audioChunk', payload),
    sendAudioDone: (payload) => socket.emit('audioDone', payload),
    connected: () => socket.connected,
    reportVoiceState: (state, relayed) =>
      socket.emit('voiceState', { state, ...(relayed === undefined ? {} : { relayed }) }),
    disconnect: () => {
      sessionToken = null
      socket.disconnect()
    },
  }
}
